import { Text, NextImage } from '@sitecore-jss/sitecore-jss-nextjs';
import { ElementRef, useRef } from 'react';
import { useI18n } from 'next-localization';
import GlobalNavigationDropdown from 'components/Generic/Header/GlobalNavigation/GlobalNavigationDropdown';
import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type SiteSelectorProps = {
  className?: string;
};

const SiteSelector = ({ className }: SiteSelectorProps): JSX.Element => {
  const dropdownTrigger = useRef<ElementRef<'div'>>(null);
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();

  const siteSelectorProperty = sitecoreContext.siteSelector;

  const siteSelectorItems = siteSelectorProperty?.vectors ?? [];

  const handleDropdownTrigger = () => {
    dropdownTrigger.current?.click();
  };

  const getClassName =
    sitecoreContext.pageTheme?.toLowerCase() === 'investment'
      ? 'background-primary'
      : 'background-black';

  const NamedSlot = () => {
    return (
      <div className={`site-selector__dropdown pt-3 me-auto ${getClassName}`}>
        <div className="site-selector__dropdown__scrollable pt-5 pb-4">
          <button
            type="button"
            aria-label={t('close')}
            className="btn btn-naked site-selector__dropdown__closer d-flex"
            onClick={handleDropdownTrigger}
          >
            <i className="bi bi-x" style={{ fontSize: '30px' }} />
          </button>
          {siteSelectorItems.map((item, index) => (
            <div key={index} className={`${index !== 0 && 'mt-3'} site-selector__item`}>
              <div className="site-selector__item__heading d-flex align-items-center px-3">
                <NextImage
                  field={item.icon}
                  className="background-to-change d-inline-flex me-2"
                  width="32"
                  height="32"
                />
                <Text tag="h4" field={item.heading} />
              </div>
              <Text
                tag="p"
                className="site-selector__item__description px-3 mt-3 body-small"
                field={item.description}
              />
              {item.links.length !== 0 && (
                <ul className="site-selector__item__links mt-2">
                  {item.links.map(
                    (cta, idx) =>
                      cta.link.value.href !== '' && (
                        <li key={idx}>
                          <ScLink
                            field={cta.link}
                            className={`${
                              cta.isSelected.value && 'active'
                            } site-selector__item__link meganav-link meganav-link--small d-flex align-items-center px-3`}
                            replaceWithSlot={false}
                            showExternalIcon={false}
                          >
                            <i className="bi bi-chevron-right ms-auto" />
                          </ScLink>
                        </li>
                      )
                  )}
                </ul>
              )}
            </div>
          ))}
          <ScLink
            field={siteSelectorProperty.changeCountryCta}
            className="site-selector__change-country site-selector__item__link meganav-link meganav-link--small d-flex align-items-center px-3 mt-3"
            showExternalIcon={false}
            replaceWithSlot={true}
          >
            <NextImage
              field={siteSelectorProperty.changeCountryIcon}
              className="background-to-change link-icon d-inline-flex me-2"
              width="28"
              height="28"
            />
            <span>{t('changeCountrySite')}</span>
            <i className="bi bi-chevron-right ms-auto" />
          </ScLink>
        </div>
      </div>
    );
  };

  return (
    siteSelectorProperty && (
      <GlobalNavigationDropdown
        componentToPassDown={<NamedSlot />}
        className={`d-none d-xl-flex ${className}`}
      >
        <div
          ref={dropdownTrigger}
          className="background-white h-100 px-2 d-flex align-items-center body-small"
        >
          <p>{t('siteSelectorTitle')}</p>
          <i className="chevron-icon bi bi-chevron-down ms-1" style={{ fontSize: '12px' }} />
        </div>
      </GlobalNavigationDropdown>
    )
  );
};
export default SiteSelector;
